import React, { useEffect, useState, useRef, useMemo } from 'react';
import ReactBeforeSliderComponent from 'react-before-after-slider-component';
import 'react-before-after-slider-component/dist/build.css';
import { colors } from '../colors';
import { CircularProgress } from '@mui/material';

const BeforeAfterSlider = ({ beforeImage, afterImage, startPosition = 50 }) => {
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const loadingRef = useRef(false);

  // Memoize the image loading function to prevent recreation on each render
  const preloadImages = useMemo(() => async () => {
    if (loadingRef.current) return;
    loadingRef.current = true;

    try {
      const beforeImg = new Image();
      const afterImg = new Image();
      
      const loadBefore = new Promise((resolve, reject) => {
        beforeImg.onload = resolve;
        beforeImg.onerror = reject;
        beforeImg.src = beforeImage;
      });

      const loadAfter = new Promise((resolve, reject) => {
        afterImg.onload = resolve;
        afterImg.onerror = reject;
        afterImg.src = afterImage;
      });

      await Promise.all([loadBefore, loadAfter]);
      setImagesLoaded(true);
    } catch (error) {
      console.error('Error preloading images:', error);
      setLoadError(true);
    } finally {
      loadingRef.current = false;
    }
  }, [beforeImage, afterImage]);

  useEffect(() => {
    if (!imagesLoaded && !loadingRef.current) {
      preloadImages();
    }
  }, [preloadImages, imagesLoaded]);

  const delimiterIconStyles = {
    width: '24px',
    height: '24px',
    background: colors.primary,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
    content: `url("data:image/svg+xml,%3Csvg width='16' height='16' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M8 7L3 12L8 17' stroke='white' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3Cpath d='M16 7L21 12L16 17' stroke='white' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`,
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)'
  };

  if (loadError) {
    return (
      <div style={{ 
        width: '100%', 
        height: '530px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f5f5f5',
        color: 'red'
      }}>
        Failed to load images
      </div>
    );
  }

  if (!imagesLoaded) {
    return (
      <div style={{ 
        width: '100%', 
        height: '530px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f5f5f5'
      }}>
        <CircularProgress />
      </div>
    );
  }

  return (
    <div style={{ 
      width: '100%', 
      height: '530px',
      maxHeight: '530px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      overflow: 'hidden',
      position: 'relative'
    }}>
      <div style={{
        width: '100%',
        height: '100%',
        maxHeight: '530px',
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center'
      }}>
        <ReactBeforeSliderComponent
          firstImage={{
            imageUrl: afterImage,
            alt: 'After carpet cleaning'
          }}
          secondImage={{
            imageUrl: beforeImage,
            alt: 'Before carpet cleaning'
          }}
          currentPercentPosition={startPosition}
          sliderWidthPercentage={0.5}
          sliderLineColor={colors.primary}
          sliderLineWidth={2}
          delimiterColor={colors.primary}
          delimiterIconStyles={delimiterIconStyles}
          delimiterIconPosition="center"
        />
      </div>
    </div>
  );
};

export default React.memo(BeforeAfterSlider);